import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

// ─── Centro multi-select component ────────────────────────────────────────────
function CentroSelector({ allCentros, selected, onChange }) {
  const [search, setSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);

  // allCentros is now an array of objects { name, employees, kpis, has_data }
  // Backwards-compatible: if a string array is passed, wrap it.
  const enriched = Array.isArray(allCentros) && allCentros.length > 0 && typeof allCentros[0] === 'string'
    ? allCentros.map(name => ({ name, employees: 0, kpis: 0, has_data: true }))
    : (allCentros || []);

  const filtered = enriched.filter(c => {
    if (!c.name) return false;
    if (onlyActive && !c.has_data) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const toggle = (name) => {
    if (selected.includes(name)) onChange(selected.filter(x => x !== name));
    else onChange([...selected, name]);
  };
  const selectAll = () => onChange([...filtered.map(c => c.name)]);
  const clearAll  = () => onChange([]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar centro..."
          data-testid="centro-search-input"
          style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', height: '34px', flex: 1 }}
        />
        <button type="button" onClick={selectAll} style={{ background: '#1d4ed8', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}>Todos</button>
        <button type="button" onClick={clearAll}  style={{ background: '#334155', color: '#94a3b8', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}>Ninguno</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: '#64748b' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }} data-testid="centro-only-active-toggle">
          <input
            type="checkbox"
            checked={onlyActive}
            onChange={e => setOnlyActive(e.target.checked)}
            style={{ accentColor: '#3b82f6', cursor: 'pointer' }}
          />
          <span>Solo centros con fichajes (último año)</span>
        </label>
        <span>
          {selected.length === filtered.length && filtered.length > 0
            ? <span style={{ color: '#22c55e' }}>✓ Todos visibles ({filtered.length})</span>
            : <span>{selected.length} seleccionado(s) · {filtered.length} visibles</span>
          }
        </span>
      </div>

      <div style={{ maxHeight: '280px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px',
        background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px' }}>
        {filtered.map(c => (
          <label key={c.name}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', borderRadius: '6px',
              cursor: 'pointer', transition: 'background-color 150ms',
              background: selected.includes(c.name) ? 'rgba(59,130,246,0.1)' : 'transparent' }}
            onMouseEnter={e => { if (!selected.includes(c.name)) e.currentTarget.style.background = 'rgba(148,163,184,0.06)'; }}
            onMouseLeave={e => { if (!selected.includes(c.name)) e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{
              width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
              border: `2px solid ${selected.includes(c.name) ? '#3b82f6' : '#334155'}`,
              background: selected.includes(c.name) ? '#3b82f6' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 150ms'
            }}>
              {selected.includes(c.name) && (
                <svg width="9" height="9" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L4 7L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <input type="checkbox" checked={selected.includes(c.name)} onChange={() => toggle(c.name)} style={{ display: 'none' }} />
            <span style={{ color: selected.includes(c.name) ? '#93c5fd' : (c.has_data ? '#94a3b8' : '#64748b'), fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={c.name}>{c.name}</span>
            {c.kpis > 0 && (
              <span style={{ color: '#22c55e', fontSize: '10px', fontFamily: 'IBM Plex Mono', flexShrink: 0 }}>
                {c.kpis.toLocaleString('es-ES')} fichajes
              </span>
            )}
            {!c.has_data && (
              <span style={{ color: '#64748b', fontSize: '10px', flexShrink: 0, fontStyle: 'italic' }}>sin fichajes</span>
            )}
          </label>
        ))}
        {filtered.length === 0 && <div style={{ color: '#475569', fontSize: '12px', padding: '12px', textAlign: 'center' }}>Sin resultados</div>}
      </div>
    </div>
  );
}

// ─── User row ──────────────────────────────────────────────────────────────────
function UserRow({ user, allCentros, onEdit, onDelete, currentEmail }) {
  const isAdmin = user.role === 'admin';
  const totalCentros = allCentros?.length || 0;
  const hasAll  = isAdmin || (user.allowed_centers?.length === totalCentros && totalCentros > 0);

  return (
    <tr className="table-row-hover" style={{ borderBottom: '1px solid #0f172a' }}>
      <td style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
            background: isAdmin ? 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' : 'linear-gradient(135deg, #1d4ed8 0%, #0ea5e9 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: '13px', fontWeight: 700
          }}>
            {(user.name || user.email || '?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ color: '#f1f5f9', fontSize: '13px', fontWeight: 500 }}>{user.name}</div>
            <div style={{ color: '#475569', fontSize: '11px', fontFamily: 'IBM Plex Mono' }}>{user.email}</div>
          </div>
        </div>
      </td>
      <td style={{ padding: '12px 16px' }}>
        <span style={{
          background: isAdmin ? '#7c3aed18' : '#1d4ed818',
          color: isAdmin ? '#c4b5fd' : '#93c5fd',
          border: `1px solid ${isAdmin ? '#7c3aed30' : '#1d4ed830'}`,
          borderRadius: '20px', padding: '3px 10px', fontSize: '11px', fontWeight: 600
        }}>
          {isAdmin ? 'Admin' : 'Usuario'}
        </span>
      </td>
      <td style={{ padding: '12px 16px' }}>
        {isAdmin ? (
          <span style={{ color: '#22c55e', fontSize: '12px' }}>✓ Todos los centros</span>
        ) : hasAll ? (
          <span style={{ color: '#22c55e', fontSize: '12px' }}>✓ Todos ({totalCentros})</span>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '400px' }}>
            {(user.allowed_centers || []).length === 0 ? (
              <span style={{ color: '#ef4444', fontSize: '12px' }}>Sin centros asignados</span>
            ) : (
              <>
                {(user.allowed_centers || []).slice(0, 3).map(c => (
                  <span key={c} style={{ background: '#1e3a5f', color: '#60a5fa', borderRadius: '4px', padding: '2px 6px', fontSize: '11px' }}>{c}</span>
                ))}
                {(user.allowed_centers || []).length > 3 && (
                  <span style={{ color: '#64748b', fontSize: '11px' }}>+{(user.allowed_centers || []).length - 3} más</span>
                )}
              </>
            )}
          </div>
        )}
      </td>
      <td style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            data-testid={`edit-user-${user.email}`}
            onClick={() => onEdit(user)}
            style={{ background: '#1e3a5f', border: '1px solid #1d4ed830', color: '#60a5fa', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px' }}
          >
            Editar accesos
          </button>
          {user.email !== currentEmail && (
            <button
              onClick={() => onDelete(user.email)}
              style={{ background: '#1c0a0a', border: '1px solid #7f1d1d30', color: '#f87171', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px' }}
            >
              Eliminar
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Main AdminPage ────────────────────────────────────────────────────────────
export default function AdminPage() {
  const { apiFetch, user: currentUser } = useAuth();
  const [users, setUsers]       = useState([]);
  const [allCentros, setCentros]= useState([]);
  const [loading, setLoading]   = useState(true);

  // Edit modal
  const [editUser, setEditUser] = useState(null);
  const [editCentros, setEditCentros] = useState([]);
  const [editRole, setEditRole]   = useState('user');
  const [editName, setEditName]   = useState('');
  const [saving, setSaving]       = useState(false);

  // New user modal
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail]     = useState('');
  const [newName, setNewName]       = useState('');
  const [newPass, setNewPass]       = useState('');
  const [newRole, setNewRole]       = useState('user');
  const [newCentros, setNewCentros] = useState([]);
  const [creating, setCreating]     = useState(false);

  // ── API integration keys ────────────────────────────────────────────────
  const [apiKeys, setApiKeys]                 = useState([]);
  const [apiKeysLoading, setApiKeysLoading]   = useState(false);
  const [showApiKeySection, setShowApiKeySection] = useState(false);
  const [newKeyName, setNewKeyName]           = useState('');
  const [newKeyNote, setNewKeyNote]           = useState('');
  const [createdPlainKey, setCreatedPlainKey] = useState(null); // shown once
  const [keyCreating, setKeyCreating]         = useState(false);

  const loadApiKeys = useCallback(async () => {
    setApiKeysLoading(true);
    try {
      const r = await apiFetch('/api/admin/integration-keys');
      if (r.ok) setApiKeys(await r.json());
    } catch (err) { console.warn(err); }
    setApiKeysLoading(false);
  }, [apiFetch]);

  useEffect(() => { if (showApiKeySection) loadApiKeys(); }, [showApiKeySection, loadApiKeys]);

  const createApiKey = async () => {
    if (!newKeyName.trim()) { toast.error('El nombre es obligatorio'); return; }
    setKeyCreating(true);
    try {
      const r = await apiFetch('/api/admin/integration-keys', {
        method: 'POST',
        body: JSON.stringify({ name: newKeyName.trim(), note: newKeyNote.trim() }),
      });
      const data = await r.json();
      if (r.ok && data.key) {
        setCreatedPlainKey(data.key);
        setNewKeyName(''); setNewKeyNote('');
        await loadApiKeys();
      } else {
        toast.error(data.detail || 'Error al crear la clave');
      }
    } catch (err) { toast.error('Error de red'); }
    setKeyCreating(false);
  };

  const revokeApiKey = async (id, name) => {
    if (!window.confirm(`¿Revocar la clave "${name}"? La app que la use dejará de funcionar inmediatamente.`)) return;
    try {
      const r = await apiFetch(`/api/admin/integration-keys/${id}`, { method: 'DELETE' });
      if (r.ok) { toast.success('Clave revocada'); loadApiKeys(); }
      else { toast.error('Error al revocar'); }
    } catch (err) { console.error('Revoke API key failed:', err); toast.error('Error de red'); }
  };

  const copyToClipboard = (txt) => {
    navigator.clipboard.writeText(txt).then(
      () => toast.success('Copiado al portapapeles'),
      () => toast.error('No se pudo copiar — selecciona y copia manualmente')
    );
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        apiFetch('/api/admin/users').then(r => r.json()),
        apiFetch('/api/admin/centros/detailed').then(r => r.json()),
      ]);
      setUsers(u);
      setCentros(c);
    } catch (err) { console.warn("[SmartClockIn] silent catch:", err); }
    setLoading(false);
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (user) => {
    setEditUser(user);
    setEditCentros(user.allowed_centers || []);
    setEditRole(user.role || 'user');
    setEditName(user.name || '');
  };

  const savePermissions = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/admin/users/${encodeURIComponent(editUser.email)}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ allowed_centers: editCentros, role: editRole, name: editName })
      });
      if (res.ok) {
        toast.success('Permisos actualizados');
        setEditUser(null);
        load();
      } else {
        let detail = `Error ${res.status}`;
        try {
          const err = await res.json();
          if (err?.detail) detail = err.detail;
        } catch { /* ignore parse errors */ }
        toast.error(detail);
      }
    } catch (e) {
      if (e?.message === 'Sesión expirada') {
        toast.error('Tu sesión ha caducado. Vuelve a iniciar sesión.');
      } else {
        toast.error('Error de conexión con el servidor');
      }
    }
    setSaving(false);
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`¿Eliminar usuario ${email}?`)) return;
    const res = await apiFetch(`/api/admin/users/${encodeURIComponent(email)}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Usuario eliminado'); load(); }
    else toast.error('Error al eliminar');
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await apiFetch('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify({ email: newEmail, name: newName, password: newPass, role: newRole, allowed_centers: newCentros })
      });
      if (res.ok) {
        toast.success(`Usuario ${newEmail} creado`);
        setShowCreate(false);
        setNewEmail(''); setNewName(''); setNewPass(''); setNewRole('user'); setNewCentros([]);
        load();
      } else {
        let detail = `Error ${res.status}`;
        try {
          const err = await res.json();
          if (err?.detail) detail = err.detail;
        } catch { /* ignore parse errors */ }
        toast.error(detail);
      }
    } catch (e) {
      if (e?.message === 'Sesión expirada') {
        toast.error('Tu sesión ha caducado. Vuelve a iniciar sesión.');
      } else {
        toast.error('Error de conexión con el servidor');
      }
    }
    setCreating(false);
  };

  return (
    <div style={{ padding: '32px', overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontFamily: 'Space Grotesk', fontSize: '24px', fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
            Administración de Accesos
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
            Gestión de usuarios y permisos RLS por centro de coste
          </p>
        </div>
        <Button
          data-testid="create-user-btn"
          onClick={() => setShowCreate(true)}
          style={{ background: '#2563eb', color: 'white', height: '40px', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            <line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/>
          </svg>
          Nuevo usuario
        </Button>
      </div>

      {/* Stats strip */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { label: 'Total usuarios', value: users.length, color: '#60a5fa' },
          { label: 'Admins', value: users.filter(u => u.role === 'admin').length, color: '#a78bfa' },
          { label: 'Centros disponibles', value: allCentros.length, color: '#34d399' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px 20px', minWidth: '160px' }}>
            <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>{s.label}</div>
            <div style={{ color: s.color, fontSize: '22px', fontWeight: 700, fontFamily: 'Space Grotesk' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Users table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>Cargando usuarios...</div>
        ) : (
          <table data-testid="admin-users-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                {['Usuario', 'Rol', 'Centros asignados', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#64748b', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <UserRow
                  key={u.email}
                  user={u}
                  allCentros={allCentros}
                  onEdit={openEdit}
                  onDelete={deleteUser}
                  currentEmail={currentUser?.email}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Permissions Dialog */}
      <Dialog open={!!editUser} onOpenChange={open => !open && setEditUser(null)}>
        <DialogContent style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk', color: '#f1f5f9' }}>
              Editar accesos — {editUser?.name || editUser?.email}
            </DialogTitle>
          </DialogHeader>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '4px' }}>
            {/* Name */}
            <div>
              <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Nombre</Label>
              <Input value={editName} onChange={e => setEditName(e.target.value)}
                style={{ marginTop: '6px', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }} />
            </div>

            {/* Role */}
            <div>
              <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Rol</Label>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                {['admin', 'user'].map(r => (
                  <button key={r} onClick={() => setEditRole(r)}
                    style={{
                      background: editRole === r ? (r === 'admin' ? '#4c1d95' : '#1e3a5f') : '#0f172a',
                      border: `1px solid ${editRole === r ? (r === 'admin' ? '#7c3aed' : '#3b82f6') : '#334155'}`,
                      color: editRole === r ? '#f1f5f9' : '#64748b',
                      borderRadius: '8px', padding: '8px 18px', cursor: 'pointer', fontSize: '13px', textTransform: 'capitalize'
                    }}>
                    {r === 'admin' ? 'Administrador' : 'Usuario'}
                  </button>
                ))}
              </div>
              {editRole === 'admin' && (
                <p style={{ color: '#f97316', fontSize: '12px', marginTop: '6px', marginBottom: 0 }}>
                  ⚠ Los administradores tienen acceso a todos los centros sin restricción.
                </p>
              )}
            </div>

            {/* Centers */}
            {editRole !== 'admin' && (
              <div>
                <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Centros permitidos ({editCentros.length}/{allCentros.length})</Label>
                <div style={{ marginTop: '8px' }}>
                  <CentroSelector allCentros={allCentros} selected={editCentros} onChange={setEditCentros} />
                </div>
              </div>
            )}
          </div>

          <DialogFooter style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setEditUser(null)} style={{ color: '#64748b' }}>Cancelar</Button>
            <Button onClick={savePermissions} disabled={saving}
              style={{ background: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {saving ? 'Guardando...' : 'Guardar permisos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk', color: '#f1f5f9' }}>Nuevo usuario</DialogTitle>
          </DialogHeader>

          <form onSubmit={createUser} style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '4px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Email corporativo</Label>
                <Input
                  data-testid="new-user-email"
                  type="email" required value={newEmail} onChange={e => setNewEmail(e.target.value)}
                  placeholder="usuario@valoriza.es"
                  style={{ marginTop: '6px', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
                />
              </div>
              <div>
                <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Nombre completo</Label>
                <Input
                  data-testid="new-user-name"
                  required value={newName} onChange={e => setNewName(e.target.value)}
                  placeholder="Juan García"
                  style={{ marginTop: '6px', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
                />
              </div>
            </div>
            <div>
              <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Contraseña inicial</Label>
              <Input
                type="password" value={newPass} onChange={e => setNewPass(e.target.value)}
                placeholder="ChangeMe2024! (si se deja vacío)"
                style={{ marginTop: '6px', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
              />
              <p style={{ color: '#475569', fontSize: '11px', marginTop: '4px' }}>Dejar vacío para usar contraseña por defecto: <code style={{ fontFamily: 'IBM Plex Mono' }}>ChangeMe2024!</code></p>
            </div>
            <div>
              <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Rol</Label>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                {['user', 'admin'].map(r => (
                  <button key={r} type="button" onClick={() => setNewRole(r)}
                    style={{
                      background: newRole === r ? (r === 'admin' ? '#4c1d95' : '#1e3a5f') : '#0f172a',
                      border: `1px solid ${newRole === r ? (r === 'admin' ? '#7c3aed' : '#3b82f6') : '#334155'}`,
                      color: newRole === r ? '#f1f5f9' : '#64748b',
                      borderRadius: '8px', padding: '8px 18px', cursor: 'pointer', fontSize: '13px'
                    }}>
                    {r === 'admin' ? 'Administrador' : 'Usuario'}
                  </button>
                ))}
              </div>
            </div>
            {newRole !== 'admin' && (
              <div>
                <Label style={{ color: '#94a3b8', fontSize: '13px' }}>Centros permitidos ({newCentros.length}/{allCentros.length})</Label>
                <div style={{ marginTop: '8px' }}>
                  <CentroSelector allCentros={allCentros} selected={newCentros} onChange={setNewCentros} />
                </div>
              </div>
            )}

            <DialogFooter style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button type="button" variant="ghost" onClick={() => setShowCreate(false)} style={{ color: '#64748b' }}>Cancelar</Button>
              <Button type="submit" disabled={creating}
                style={{ background: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {creating ? 'Creando...' : 'Crear usuario'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── INTEGRATION API KEYS ──────────────────────────────────────── */}
      <div data-testid="integration-keys-section" style={{
        background: '#1e293b', border: '1px solid #334155', borderRadius: 12,
        marginTop: 28, padding: '18px 22px',
      }}>
        <button
          data-testid="toggle-api-keys"
          onClick={() => setShowApiKeySection(v => !v)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'transparent', border: 'none', color: '#f1f5f9', cursor: 'pointer', padding: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
            </svg>
            <span style={{ fontSize: 15, fontWeight: 600 }}>API Keys de Integración</span>
            <span style={{ fontSize: 12, color: '#64748b', fontWeight: 400 }}>· acceso server-to-server al CSV diario</span>
          </div>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"
            style={{ transform: showApiKeySection ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 200ms' }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        {showApiKeySection && (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Newly created key — shown ONCE */}
            {createdPlainKey && (
              <div data-testid="new-key-banner" style={{
                background: '#1f2937', border: '1px solid #fbbf24', borderRadius: 8, padding: '14px 16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fbbf24', marginBottom: 8, fontWeight: 600 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  Copia esta clave AHORA — no se mostrará de nuevo
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <code data-testid="new-key-value" style={{
                    flex: 1, fontSize: 13, fontFamily: 'IBM Plex Mono', background: '#0f172a',
                    padding: '10px 12px', borderRadius: 6, border: '1px solid #334155',
                    color: '#f1f5f9', overflowX: 'auto', whiteSpace: 'nowrap',
                  }}>
                    {createdPlainKey}
                  </code>
                  <Button onClick={() => copyToClipboard(createdPlainKey)} style={{ background: '#fbbf24', color: '#0f172a', fontWeight: 600 }}>
                    Copiar
                  </Button>
                  <Button variant="ghost" onClick={() => setCreatedPlainKey(null)} style={{ color: '#94a3b8' }}>Hecho</Button>
                </div>
              </div>
            )}

            {/* Create key form */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px' }}>
                <Label style={{ color: '#94a3b8', fontSize: 12 }}>Nombre de la clave</Label>
                <Input data-testid="new-key-name" value={newKeyName} onChange={e => setNewKeyName(e.target.value)}
                  placeholder="ej. App Nóminas Daily Pull" style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', marginTop: 4 }} />
              </div>
              <div style={{ flex: '2 1 280px' }}>
                <Label style={{ color: '#94a3b8', fontSize: 12 }}>Nota (opcional)</Label>
                <Input data-testid="new-key-note" value={newKeyNote} onChange={e => setNewKeyNote(e.target.value)}
                  placeholder="Para qué se usa, contacto, etc." style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', marginTop: 4 }} />
              </div>
              <Button data-testid="create-key-btn" onClick={createApiKey} disabled={keyCreating || !newKeyName.trim()}
                style={{ background: '#2563eb', color: 'white', height: 38 }}>
                {keyCreating ? 'Creando…' : 'Crear clave'}
              </Button>
            </div>

            {/* Keys list */}
            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, overflow: 'hidden' }}>
              {apiKeysLoading ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#64748b', fontSize: 13 }}>Cargando…</div>
              ) : apiKeys.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#64748b', fontSize: 13 }}>Aún no hay claves creadas</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #334155' }}>
                      {['Nombre','Prefijo','Llamadas','Último uso','Estado','Acciones'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.05 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {apiKeys.map(k => (
                      <tr key={k.id} style={{ borderBottom: '1px solid #1e293b' }}>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 500 }}>{k.name}</div>
                          {k.note && <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{k.note}</div>}
                        </td>
                        <td style={{ padding: '10px 12px', fontFamily: 'IBM Plex Mono', color: '#94a3b8', fontSize: 11 }}>{k.key_prefix}</td>
                        <td style={{ padding: '10px 12px', color: '#cbd5e1' }}>{(k.call_count || 0).toLocaleString('es-ES')}</td>
                        <td style={{ padding: '10px 12px', color: '#94a3b8' }}>
                          {k.last_used_at
                            ? <span title={`IP: ${k.last_used_ip || 'desconocida'}`}>{new Date(k.last_used_at).toLocaleString('es-ES')}</span>
                            : <span style={{ color: '#475569' }}>Nunca</span>}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          {k.active
                            ? <span style={{ color: '#22c55e', fontWeight: 600 }}>● Activa</span>
                            : <span style={{ color: '#64748b' }}>○ Revocada</span>}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          {k.active && (
                            <Button data-testid={`revoke-key-${k.id}`} onClick={() => revokeApiKey(k.id, k.name)}
                              variant="ghost" style={{ color: '#ef4444', fontSize: 11, height: 26, padding: '0 8px' }}>
                              Revocar
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Info panel */}
            <div style={{ background: '#0f172a', border: '1px solid #1e3a5f', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ color: '#60a5fa', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>📖 Cómo usar las API Keys</div>
              <div style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.6 }}>
                La app cliente debe enviar la clave en el header <code style={{ background: '#1e293b', padding: '1px 5px', borderRadius: 3, fontFamily: 'IBM Plex Mono' }}>X-Api-Key</code>:
                <pre style={{ background: '#1e293b', padding: 10, borderRadius: 6, marginTop: 6, color: '#cbd5e1', fontSize: 11, overflowX: 'auto' }}>{`# Metadata (rápido)
GET /api/integrations/feed/raw-csv/info

# Descargar CSV crudo (text/csv; charset=cp850)
GET /api/integrations/feed/raw-csv

curl -H "X-Api-Key: sci_..." https://smartclockin.valorizasm.com/api/integrations/feed/raw-csv -o employees.csv`}</pre>
                Headers de respuesta: <code>X-Source</code>, <code>X-Source-Date</code>, <code>X-Fetched-At</code>, <code>X-Row-Count</code>, <code>X-Size-Bytes</code>.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
